import {
  useCardSession,
  useCardStatus,
  type ComponentFn,
} from '@littlebird/action-cards-view/react';
import { motion, useReducedMotion } from 'motion/react';
import { Fragment, useState } from 'react';
import { flushSync } from 'react-dom';

import { XStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

import { useElementId } from '../fieldIds';
import { useSectionsLayout } from '../internals/objectContext';
import {
  OPTIONAL_SLOTS,
  hiddenSlots,
  showsSlot,
  type OptionalSlot,
} from '../internals/recipientSlots';
import { useCardMetrics } from '../internals/useCardMetrics';
import { DIVIDED_CLASS, DIVIDED_GAP, sizingClass, TEXT_STYLE } from '../tokens';

import { ChipList } from './ChipList';

import type { catalog } from '@littlebird/action-cards-view';

type Slot = 'to' | OptionalSlot;

const LABEL: Record<Slot, string> = { to: 'To', cc: 'Cc', bcc: 'Bcc' };

const ROW_SECTIONS_CLASS =
  '[&>*]:px-[var(--ac-flush-px)] [&>*]:py-[var(--ac-flush-py)]';

const REVEAL_LINK_CLASS = `${TEXT_STYLE.meta} h-[calc(24*var(--ac-u))] text-foreground-muted underline-offset-2 transition-colors duration-[120ms] hover:text-foreground hover:underline motion-reduce:duration-[60ms]`;

// An email's To, Cc and Bcc, each drawn by ChipList. Cc and Bcc stay hidden until they
// hold an address or the user reveals them from the quiet links at the end of the To row;
// the revealed set is local and never written back
export const Recipients: ComponentFn<typeof catalog, 'Recipients'> = ({
  props,
  bindings,
  emit,
  on,
}) => {
  const id = useElementId(props);
  const reducedMotion = useReducedMotion();
  const metrics = useCardMetrics();
  const session = useCardSession();
  const status = useCardStatus();
  const sections = useSectionsLayout();
  // Rows pad themselves only when each is its own section between hairlines
  const rowSections = sections.divided && sections.flush;
  const [revealed, setRevealed] = useState<OptionalSlot[]>([]);
  const editable =
    props.editable !== false && !session.readOnly && status !== 'running';

  const slotProps = {
    to: {
      value: props.to,
      items: props.toItems,
      checks: props.toChecks,
      error: props.toError,
    },
    cc: {
      value: props.cc,
      items: props.ccItems,
      checks: props.ccChecks,
      error: props.ccError,
    },
    bcc: {
      value: props.bcc,
      items: props.bccItems,
      checks: props.bccChecks,
      error: props.bccError,
    },
  };
  const bound = OPTIONAL_SLOTS.filter((slot) => bindings?.[slot] !== undefined);
  const showing = bound.filter((slot) =>
    showsSlot({
      entries: slotProps[slot].value?.length ?? 0,
      error: slotProps[slot].error,
      revealed: revealed.includes(slot),
      editable,
    })
  );
  const hidden = hiddenSlots({ bound, showing, editable });
  const slotId = (slot: Slot) => (slot === 'to' ? id : `${id}-${slot}`);

  // The revealed list's input exists only once it renders, so render it before focusing
  const reveal = (slot: OptionalSlot) => {
    flushSync(() => setRevealed([...revealed, slot]));
    document.getElementById(slotId(slot))?.focus();
  };

  const revealControls = (
    <>
      {hidden.length > 0 ? (
        <XStack
          className={cn(
            'shrink-0 items-center gap-[var(--ac-gap-xs)]',
            props.layout !== 'inline' && 'ml-auto'
          )}
        >
          {hidden.map((slot, index) => (
            <Fragment key={slot}>
              {index > 0 ? (
                <span
                  aria-hidden="true"
                  className={cn(TEXT_STYLE.meta, 'text-foreground-muted')}
                >
                  ·
                </span>
              ) : null}
              <button
                type="button"
                className={cn(
                  REVEAL_LINK_CLASS,
                  // The phone's 44-unit tap target extends past the row instead of making it taller
                  metrics.mobile &&
                    'my-[calc(-10*var(--ac-u))] h-[calc(44*var(--ac-u))]'
                )}
                onClick={() => reveal(slot)}
              >
                {LABEL[slot]}
              </button>
            </Fragment>
          ))}
        </XStack>
      ) : null}
    </>
  );

  const list = (slot: Slot) => {
    const path = bindings?.[slot];
    return (
      <ChipList
        props={{
          ...slotProps[slot],
          value: slotProps[slot].value ?? [],
          label: LABEL[slot],
          editable: props.editable,
          suggestions: props.suggestions,
          validateOn: props.validateOn,
          appearance: props.layout === 'inline' ? 'inline' : 'rows',
          recipient: true,
          sizing: props.layout === 'inline' ? 'hug' : 'fill',
          elementId: slotId(slot),
        }}
        bindings={path === undefined ? {} : { value: path }}
        emit={emit}
        on={on}
      >
        {slot === 'to' ? revealControls : null}
      </ChipList>
    );
  };

  // Every inline group starts with a separator dot. The block is shifted left by
  // one dot, so the dot at the start of a line falls outside the clip. To and Cc
  // share a line when they fit; when they don't, they stack with no leading dot.
  if (props.layout === 'inline') {
    return (
      <motion.div
        layout
        transition={{ duration: reducedMotion ? 0 : 0.18 }}
        className={cn(
          'min-w-0 [clip-path:inset(-8px_-8px_-8px_0)]',
          !sections.divided && 'border-b border-border-subtle',
          sizingClass(props.sizing)
        )}
      >
        <div className="ml-[calc(-12*var(--ac-u))] flex flex-row flex-wrap items-baseline gap-x-[var(--ac-gap-xs)]">
          {(['to', ...showing] as const).map((slot) => (
            <span
              key={slot}
              className="inline-flex min-w-0 items-baseline gap-[var(--ac-gap-xs)]"
            >
              <span
                aria-hidden="true"
                className="w-[calc(8*var(--ac-u))] shrink-0 text-center text-foreground-muted"
              >
                ·
              </span>
              {list(slot)}
            </span>
          ))}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      layout
      transition={{ duration: reducedMotion ? 0 : 0.18 }}
      data-sections={rowSections || undefined}
      className={cn(
        'flex flex-col min-w-0',
        sections.divided
          ? cn(DIVIDED_CLASS, !sections.flush && DIVIDED_GAP.sm)
          : 'gap-[var(--ac-gap-sm)] border-b border-border-subtle',
        rowSections && ROW_SECTIONS_CLASS,
        sizingClass(props.sizing)
      )}
    >
      {list('to')}
      {showing.map((slot) => (
        <Fragment key={slot}>{list(slot)}</Fragment>
      ))}
    </motion.div>
  );
};
